/**
 * LoadSEER API Client
 * Implements API calls defined in loadseer-api.yaml
 */

import type {
  LoadSEERApiConfig,
  SearchNodesParams,
  GetChildNodesParams,
  GridAssetNodeSummaryDtoListResultPaginated,
  GridAssetNodeSummaryDto,
  NodeGeometryFeature,
  NodeTypeDtoListResult,
  NodeHierarchySummaryDto,
  ApiError,
  ScenarioDtoListResultPaginated,
  GetScenariosParams,
  AdjustmentDtoListResult,
  GetSpatialAdjustmentsParams,
  GetUserAdjustmentsParams,
  GetNewGrowthAdjustmentsParams,
  AdjustmentTypeDtoListResult,
  GetAdjustmentTypesParams,
  GetAncestorsParams,
} from '../types';
import { validateUUID, validatePaginationParams, sanitizeSearchQuery } from '../utils';
import { API_REQUEST_TIMEOUT_MS } from '../constants';

export class LoadSEERApiClient {
  private config: LoadSEERApiConfig;

  constructor(config: LoadSEERApiConfig) {
    this.config = config;
  }

  /**
   * Updates the API configuration
   */
  updateConfig(config: Partial<LoadSEERApiConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Makes an authenticated API request
   */
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    // Trim whitespace from baseUrl and endpoint to prevent URL issues
    const url = `${this.config.baseUrl.trim()}${endpoint.trim()}`;

    const headers: Record<string, string> = {
      'X-API-Key': this.config.apiToken,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };

    // Log for debugging (only in development)
    // Security: Never log API tokens, even partially
    // Security: Only log endpoint path, not full URL (query params may contain PII)
    if (import.meta.env.DEV) {
      // Handle both absolute URLs and relative paths (for Vite proxy)
      const urlObj = url.startsWith('/') ? new URL(url, window.location.origin) : new URL(url);
      console.log('[LoadSEER API] Request:', {
        endpoint: urlObj.pathname, // Only log path, not query params
        authenticated: !!this.config.apiToken,
        headers: Object.keys(headers),
      });
    }

    try {
      // Create AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), API_REQUEST_TIMEOUT_MS);

      const response = await fetch(url, {
        ...options,
        headers,
        credentials: 'omit', // Don't send cookies
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const error: ApiError = {
          message: `API request failed: ${response.statusText}`,
          statusCode: response.status,
        };

        // Try to extract error details from response
        try {
          const errorData = await response.json();
          error.details = errorData;
          error.message = errorData.message || error.message;
        } catch {
          // Response body not JSON, use status text
        }

        throw error;
      }

      return await response.json();
    } catch (error) {
      if ((error as ApiError).statusCode) {
        throw error; // Re-throw API errors
      }

      // Handle abort/timeout errors
      if (error instanceof Error && error.name === 'AbortError') {
        const apiError: ApiError = {
          message: `Request timeout - the API did not respond within ${API_REQUEST_TIMEOUT_MS / 1000} seconds`,
          statusCode: 408,
          details: { url, timeout: API_REQUEST_TIMEOUT_MS },
        };
        if (import.meta.env.DEV) {
          console.error('[LoadSEER API] Timeout:', {
            message: apiError.message,
            statusCode: apiError.statusCode,
            url: url,
          });
        }
        throw apiError;
      }

      // Network or other errors
      const apiError: ApiError = {
        message: error instanceof Error ? error.message : 'Network request failed',
        details: error,
      };
      if (import.meta.env.DEV) {
        console.error('[LoadSEER API] Error:', {
          message: apiError.message,
          url,
          errorType: error instanceof Error ? error.name : typeof error,
        });
      }
      throw apiError;
    }
  }

  /**
   * GET /hierarchy/grid-assets/search
   * Searches for grid asset nodes by name
   */
  async searchNodes(
    params: SearchNodesParams
  ): Promise<GridAssetNodeSummaryDtoListResultPaginated> {
    const { query, page = 1, pageSize = 100 } = params;

    // Validate pagination
    const paginationValidation = validatePaginationParams(page, pageSize);
    if (!paginationValidation.valid) {
      throw new Error(paginationValidation.error);
    }

    // Sanitize search query
    const sanitizedQuery = sanitizeSearchQuery(query);
    if (!sanitizedQuery) {
      throw new Error('Search query cannot be empty');
    }

    const searchParams = new URLSearchParams({
      query: sanitizedQuery,
      page: page.toString(),
      pageSize: pageSize.toString(),
    });

    return this.request<GridAssetNodeSummaryDtoListResultPaginated>(
      `/hierarchy/grid-assets/search?${searchParams.toString()}`
    );
  }

  /**
   * GET /hierarchy/grid-assets/{lsid}/children
   * Gets child nodes of a specific node, optionally filtered by type
   */
  async getChildNodes(
    params: GetChildNodesParams
  ): Promise<GridAssetNodeSummaryDtoListResultPaginated> {
    const { lsid, childNodeType, page = 1, pageSize = 100 } = params;

    // Validate LSID
    const uuidValidation = validateUUID(lsid);
    if (!uuidValidation.valid) {
      throw new Error(uuidValidation.error);
    }

    // Validate pagination
    const paginationValidation = validatePaginationParams(page, pageSize);
    if (!paginationValidation.valid) {
      throw new Error(paginationValidation.error);
    }

    const searchParams = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });

    if (childNodeType) {
      searchParams.set('childNodeType', childNodeType);
    }

    return this.request<GridAssetNodeSummaryDtoListResultPaginated>(
      `/hierarchy/grid-assets/${lsid}/children?${searchParams.toString()}`
    );
  }

  /**
   * GET /hierarchy/grid-assets/hierarchy?startLsid={lsid}
   * Fetches all descendants using the hierarchy endpoint (more efficient than recursive /children calls)
   * Filters results by childNodeType if provided
   */
  async getAllDescendants(
    lsid: string,
    childNodeType?: string
  ): Promise<GridAssetNodeSummaryDtoListResultPaginated> {
    if (import.meta.env.DEV) {
      console.log(`Fetching hierarchy from ${lsid}, filtering by: ${childNodeType || 'all types'}`);
    }

    // Fetch the entire hierarchy tree starting from this node
    const hierarchy = await this.request<NodeHierarchySummaryDto>(
      `/hierarchy/grid-assets/hierarchy?startLsid=${lsid}`
    );

    // Flatten the hierarchy tree into a list, filtering by node type if specified
    const results: GridAssetNodeSummaryDto[] = [];
    const flatten = (node: NodeHierarchySummaryDto) => {
      // Add this node if it matches the filter (or no filter)
      // Note: We skip the root node (startLsid) as it's typically already loaded
      if (node.lsid !== lsid && (!childNodeType || node.nodeType === childNodeType)) {
        results.push({
          lsid: node.lsid,
          name: node.name,
          nodeType: node.nodeType,
          externalId: node.externalId,
          isActive: true, // Hierarchy endpoint doesn't include this, assume true
          labels: [],
        });
      }

      // Recursively flatten children
      if (node.children && node.children.length > 0) {
        node.children.forEach(flatten);
      }
    };

    flatten(hierarchy);

    if (import.meta.env.DEV) {
      console.log(
        `Found ${results.length} descendants (filtered by ${childNodeType || 'all types'})`
      );
    }

    return {
      results,
      totalResults: results.length,
      returnedResults: results.length,
      page: 1,
      pageSize: results.length,
      nextPage: null,
      lastPage: 1,
    };
  }

  /**
   * GET /hierarchy/grid-assets/{lsid}/ancestors
   * Gets ancestor nodes (parent, grandparent, etc.) of a specific node
   * Returns ancestors in order from immediate parent to root
   *
   * @param params - Parameters for ancestor retrieval
   * @param params.lsid - Node LoadSEER ID
   * @param params.stopAtLabel - Optional label to stop traversal at first node with this label
   * @param params.endOnly - If true, only return the furthest ancestor (default: false)
   * @param params.maxDepth - Optional maximum depth to traverse
   * @param params.page - Page number (default: 1)
   * @param params.pageSize - Results per page (default: 100)
   */
  async getAncestors(
    params: GetAncestorsParams
  ): Promise<GridAssetNodeSummaryDtoListResultPaginated> {
    const { lsid, stopAtLabel, endOnly = false, maxDepth, page = 1, pageSize = 100 } = params;

    // Validate LSID
    const uuidValidation = validateUUID(lsid);
    if (!uuidValidation.valid) {
      throw new Error(uuidValidation.error);
    }

    // Validate pagination
    const paginationValidation = validatePaginationParams(page, pageSize);
    if (!paginationValidation.valid) {
      throw new Error(paginationValidation.error);
    }

    const searchParams = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });

    if (stopAtLabel) {
      searchParams.set('stopAtLabel', stopAtLabel);
    }

    if (endOnly) {
      searchParams.set('endOnly', 'true');
    }

    if (maxDepth !== undefined) {
      searchParams.set('maxDepth', maxDepth.toString());
    }

    return this.request<GridAssetNodeSummaryDtoListResultPaginated>(
      `/hierarchy/grid-assets/${lsid}/ancestors?${searchParams.toString()}`
    );
  }

  /**
   * GET /hierarchy/node-geometries/{lsid}
   * Gets geometry for a specific node
   * Note: Response is a GeoJSON FeatureCollection with features array
   */
  async getNodeGeometry(lsid: string): Promise<NodeGeometryFeature[]> {
    // Validate LSID
    const uuidValidation = validateUUID(lsid);
    if (!uuidValidation.valid) {
      throw new Error(uuidValidation.error);
    }

    // API returns a GeoJSON FeatureCollection
    interface GeoJSONFeature {
      type: 'Feature';
      geometry: {
        type: string;
        coordinates: unknown;
      };
      properties: Record<string, unknown>;
    }

    interface GeoJSONFeatureCollection {
      type: 'FeatureCollection';
      features: GeoJSONFeature[];
    }

    const featureCollection = await this.request<GeoJSONFeatureCollection>(
      `/hierarchy/node-geometries/${lsid}`
    );

    // Convert GeoJSON features to NodeGeometryFeature format
    return featureCollection.features.map(feature => ({
      geometry: feature.geometry as NodeGeometryFeature['geometry'],
      boundingBox: null, // Not provided in the response
      attributes: feature.properties,
    }));
  }

  /**
   * GET /hierarchy/node-types
   * Gets all available node types for mapping codes to names
   */
  async getNodeTypes(): Promise<NodeTypeDtoListResult> {
    return this.request<NodeTypeDtoListResult>('/hierarchy/node-types');
  }

  /**
   * GET /scenarios
   * Gets all scenarios with optional filters
   */
  async getScenarios(params?: GetScenariosParams): Promise<ScenarioDtoListResultPaginated> {
    const queryParams = new URLSearchParams();

    if (params?.includeInactive !== undefined) {
      queryParams.append('includeInactive', String(params.includeInactive));
    }
    if (params?.page !== undefined) {
      queryParams.append('page', String(params.page));
    }
    if (params?.pageSize !== undefined) {
      queryParams.append('pageSize', String(params.pageSize));
    }

    const queryString = queryParams.toString();
    const endpoint = queryString ? `/scenarios?${queryString}` : '/scenarios';

    return this.request<ScenarioDtoListResultPaginated>(endpoint);
  }

  /**
   * GET /adjustments/spatial-adjustments
   * Gets spatial adjustments for a scenario (optionally filtered by run)
   */
  async getSpatialAdjustments(
    params: GetSpatialAdjustmentsParams
  ): Promise<AdjustmentDtoListResult> {
    const { scenarioId, ...rest } = params;

    // scenarioId is required
    if (!scenarioId) {
      throw new Error('scenarioId is required for spatial adjustments');
    }

    const queryParams = new URLSearchParams();
    queryParams.append('scenarioId', String(scenarioId));

    // Add optional parameters
    if (rest.runId !== undefined) {
      queryParams.append('runId', String(rest.runId));
    }
    if (rest.lsid) {
      const uuidValidation = validateUUID(rest.lsid);
      if (!uuidValidation.valid) {
        throw new Error(uuidValidation.error);
      }
      queryParams.append('lsid', rest.lsid);
    }
    if (rest.includeDownstream !== undefined) {
      queryParams.append('includeDownstream', String(rest.includeDownstream));
    }
    if (rest.adjustmentTypeId !== undefined) {
      queryParams.append('adjustmentTypeId', String(rest.adjustmentTypeId));
    }
    if (rest.activeOnly !== undefined) {
      queryParams.append('activeOnly', String(rest.activeOnly));
    }
    if (rest.page !== undefined) {
      queryParams.append('page', String(rest.page));
    }
    if (rest.pageSize !== undefined) {
      queryParams.append('pageSize', String(rest.pageSize));
    }

    return this.request<AdjustmentDtoListResult>(
      `/adjustments/spatial-adjustments?${queryParams.toString()}`
    );
  }

  /**
   * GET /adjustments/user-adjustments
   * Gets user adjustments for a scenario (optionally filtered by run)
   */
  async getUserAdjustments(params: GetUserAdjustmentsParams): Promise<AdjustmentDtoListResult> {
    const { scenarioId, ...rest } = params;

    // scenarioId is required
    if (!scenarioId) {
      throw new Error('scenarioId is required for user adjustments');
    }

    const queryParams = new URLSearchParams();
    queryParams.append('scenarioId', String(scenarioId));

    // Add optional parameters
    if (rest.runId !== undefined) {
      queryParams.append('runId', String(rest.runId));
    }
    if (rest.lsid) {
      const uuidValidation = validateUUID(rest.lsid);
      if (!uuidValidation.valid) {
        throw new Error(uuidValidation.error);
      }
      queryParams.append('lsid', rest.lsid);
    }
    if (rest.includeDownstream !== undefined) {
      queryParams.append('includeDownstream', String(rest.includeDownstream));
    }
    if (rest.portfolioId !== undefined) {
      queryParams.append('portfolioId', String(rest.portfolioId));
    }
    if (rest.adjustmentTypeId !== undefined) {
      queryParams.append('adjustmentTypeId', String(rest.adjustmentTypeId));
    }
    if (rest.activeOnly !== undefined) {
      queryParams.append('activeOnly', String(rest.activeOnly));
    }
    if (rest.page !== undefined) {
      queryParams.append('page', String(rest.page));
    }
    if (rest.pageSize !== undefined) {
      queryParams.append('pageSize', String(rest.pageSize));
    }

    return this.request<AdjustmentDtoListResult>(
      `/adjustments/user-adjustments?${queryParams.toString()}`
    );
  }

  /**
   * GET /adjustments/new-growth-adjustments
   * Gets new growth adjustments (no scenario/run required)
   */
  async getNewGrowthAdjustments(
    params?: GetNewGrowthAdjustmentsParams
  ): Promise<AdjustmentDtoListResult> {
    const queryParams = new URLSearchParams();

    if (params?.lsid) {
      const uuidValidation = validateUUID(params.lsid);
      if (!uuidValidation.valid) {
        throw new Error(uuidValidation.error);
      }
      queryParams.append('lsid', params.lsid);
    }
    if (params?.includeDownstream !== undefined) {
      queryParams.append('includeDownstream', String(params.includeDownstream));
    }
    if (params?.portfolioId !== undefined) {
      queryParams.append('portfolioId', String(params.portfolioId));
    }
    if (params?.adjustmentTypeId !== undefined) {
      queryParams.append('adjustmentTypeId', String(params.adjustmentTypeId));
    }
    if (params?.activeOnly !== undefined) {
      queryParams.append('activeOnly', String(params.activeOnly));
    }
    if (params?.page !== undefined) {
      queryParams.append('page', String(params.page));
    }
    if (params?.pageSize !== undefined) {
      queryParams.append('pageSize', String(params.pageSize));
    }

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `/adjustments/new-growth-adjustments?${queryString}`
      : '/adjustments/new-growth-adjustments';

    return this.request<AdjustmentDtoListResult>(endpoint);
  }

  /**
   * GET /adjustments/types
   * Gets all adjustment types
   */
  async getAdjustmentTypes(
    params?: GetAdjustmentTypesParams
  ): Promise<AdjustmentTypeDtoListResult> {
    const queryParams = new URLSearchParams();

    if (params?.includeInactive !== undefined) {
      queryParams.append('includeInactive', String(params.includeInactive));
    }

    const queryString = queryParams.toString();
    const endpoint = queryString ? `/adjustments/types?${queryString}` : '/adjustments/types';

    return this.request<AdjustmentTypeDtoListResult>(endpoint);
  }

  /**
   * Tests the connection by fetching node types
   * Returns true if successful, throws error otherwise
   */
  async testConnection(): Promise<boolean> {
    await this.getNodeTypes();
    return true;
  }
}
